import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, ArrowRight, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const GlobalSearch = ({ className }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  const fetchResults = useCallback(async (q) => {
    if (!q || q.trim().length < 2) {
      setResults([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      // Use a relative URL so requests go through the same origin in
      // production (porticosystems.com) AND in preview, avoiding
      // build-time-baked URLs and 308 cross-origin redirects.
      const res = await fetch(`/api/search?q=${encodeURIComponent(q)}&limit=8`);
      if (!res.ok) {
        setResults([]);
      } else {
        const data = await res.json();
        setResults(data.results || []);
      }
    } catch {
      setResults([]);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchResults(query), 200);
    return () => clearTimeout(debounceRef.current);
  }, [query, fetchResults]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (result) => {
    setIsOpen(false);
    setQuery('');
    setResults([]);
    navigate(result.url);
  };

  const handleKeyDown = (e) => {
    if (!isOpen || results.length === 0) {
      if (e.key === 'Escape') {
        setIsOpen(false);
        inputRef.current?.blur();
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      handleSelect(results[selectedIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  const getCategoryColor = (category) => {
    const map = {
      'ASOTA': 'bg-emerald-100 text-emerald-700',
      'Natural Coconut': 'bg-amber-100 text-amber-700',
      'PA6': 'bg-violet-100 text-violet-700',
      'Recycled Rubber': 'bg-stone-200 text-stone-700',
      'PVC': 'bg-sky-100 text-sky-700',
      'Vinyl': 'bg-blue-100 text-blue-700',
      'Aluminum': 'bg-gray-200 text-gray-600',
      'Rubber': 'bg-orange-100 text-orange-700',
    };
    for (const [key, val] of Object.entries(map)) {
      if (category?.includes(key)) return val;
    }
    return 'bg-stone-100 text-stone-600';
  };

  const showDropdown = isOpen && (results.length > 0 || isLoading || query.length >= 2);

  return (
    <div ref={containerRef} className={cn("relative", className)} data-testid="global-search">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/70 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); setSelectedIndex(-1); }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search products, materials, zones..."
          className="w-full pl-9 pr-9 py-2.5 text-sm bg-white text-foreground border border-border rounded-full shadow-md focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all placeholder:text-muted-foreground"
          data-testid="global-search-input"
        />
        {query && (
          <button
            onClick={() => { setQuery(''); setResults([]); setIsOpen(false); inputRef.current?.focus(); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            data-testid="global-search-clear"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Results Dropdown */}
      {showDropdown && (
        <div
          className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-lg shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150"
          data-testid="global-search-results"
        >
          {isLoading && results.length === 0 && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {!isLoading && results.length === 0 && query.length >= 2 && (
            <div className="py-8 text-center text-sm text-muted-foreground" data-testid="global-search-no-results">
              No products found for "{query}"
            </div>
          )}

          {results.length > 0 && (
            <ul className="py-1 max-h-[400px] overflow-y-auto">
              {results.map((result, idx) => (
                <li key={result.url}>
                  <button
                    onClick={() => handleSelect(result)}
                    onMouseEnter={() => setSelectedIndex(idx)}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-3 text-left transition-colors",
                      idx === selectedIndex ? "bg-primary/5" : "hover:bg-muted/50"
                    )}
                    data-testid={`search-result-${idx}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground truncate">
                          {result.product_name}
                        </span>
                        <span className={cn(
                          "text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap",
                          getCategoryColor(result.category)
                        )}>
                          {result.category?.split(',')[0]?.replace('ASOTA® polypropylene fibers', 'ASOTA').replace('Natural Coconut Fibers', 'Coco').replace('PA6 Space Dyed Nylon', 'Nylon').substring(0, 20)}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                        {result.title}
                      </p>
                      {result.matched_terms?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {result.matched_terms.slice(0, 4).map((term) => (
                            <span key={`term-${term}`} className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                              {term}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <ArrowRight className={cn(
                      "w-4 h-4 flex-shrink-0 transition-opacity",
                      idx === selectedIndex ? "opacity-100 text-primary" : "opacity-0"
                    )} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {results.length > 0 && (
            <div className="border-t border-border px-4 py-2 flex items-center justify-between">
              <span className="text-[11px] text-muted-foreground">
                {results.length} result{results.length !== 1 ? 's' : ''}
              </span>
              <span className="text-[11px] text-muted-foreground">
                <kbd className="px-1 py-0.5 bg-muted rounded text-[10px]">↑↓</kbd> navigate
                <span className="mx-1">·</span>
                <kbd className="px-1 py-0.5 bg-muted rounded text-[10px]">↵</kbd> select
                <span className="mx-1">·</span>
                <kbd className="px-1 py-0.5 bg-muted rounded text-[10px]">esc</kbd> close
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
